// ============================================================
// CLUBHUB – Nachrichten: Kanal und Zweck als Zeichen
// ============================================================
import { Mail, MessageSquareText, Newspaper, Send, Stamp } from "lucide-react";
import type { DeliveryStatus, MailingChannel, MailingStatus } from "@/generated/prisma/enums";
import { DELIVERY_STATUS, MAILING_CHANNEL, MAILING_STATUS } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { TonBadge } from "@/components/mitglieder/status-badge";
import type { Zustellweg, Zweck } from "@/lib/nachrichten/views";

export const KANAL_ICON = { EMAIL: Mail, SMS: MessageSquareText, LETTER: Stamp } as const;

export const WEG_LABEL: Record<Zustellweg, string> = { EMAIL: "E-Mail", SMS: "SMS", LETTER: "Brief" };

export function KanalZeichen({ channel, className }: { channel: MailingChannel; className?: string }) {
  const Icon = KANAL_ICON[channel];
  return (
    <span
      title={MAILING_CHANNEL[channel]}
      className={cn("bg-bg-tertiary text-text-secondary grid h-7 w-7 shrink-0 place-items-center rounded-lg", className)}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">{MAILING_CHANNEL[channel]}</span>
    </span>
  );
}

export function WegEtikett({ weg }: { weg: Zustellweg }) {
  const Icon = KANAL_ICON[weg];
  return (
    <span className="text-caption text-text-secondary inline-flex items-center gap-1.5 whitespace-nowrap">
      <Icon className="text-text-tertiary h-3.5 w-3.5" aria-hidden />
      {WEG_LABEL[weg]}
    </span>
  );
}

export function ZweckEtikett({ zweck }: { zweck: Zweck }) {
  if (zweck === "INFO") return null;
  return (
    <span className="bg-review-subtle text-review text-caption inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium whitespace-nowrap">
      <Newspaper className="h-3 w-3" aria-hidden />
      Newsletter
    </span>
  );
}

export function MailingStatusBadge({ status }: { status: MailingStatus }) {
  const s = MAILING_STATUS[status];
  return (
    <TonBadge ton={s.ton}>
      {status === "SENDING" && <Send className="mr-1 h-3 w-3 animate-pulse" aria-hidden />}
      {s.label}
    </TonBadge>
  );
}

/** Zustellstatus — Briefe kennen kein „zugestellt“, nur „übergeben“. */
export function ZustellBadge({ status, weg }: { status: DeliveryStatus; weg: Zustellweg }) {
  const s = DELIVERY_STATUS[status];
  const label = weg === "LETTER" && status === "SENT" ? "Übergeben" : s.label;
  const ton = weg === "LETTER" && status === "SENT" ? "success" : s.ton;
  return <TonBadge ton={ton}>{label}</TonBadge>;
}
